import { AlertTriangle, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { ErrorAction, ErrorSeverity } from '@/shared/types/error-action'
import { Button } from '@/shared/ui/button'

const SEVERITY_STYLES: Record<
  ErrorSeverity,
  { icon: typeof XCircle; accent: string; border: string }
> = {
  error: { icon: XCircle, accent: 'text-red-600', border: 'border-l-red-500' },
  warning: { icon: AlertTriangle, accent: 'text-amber-600', border: 'border-l-amber-500' },
  info: { icon: Info, accent: 'text-blue-600', border: 'border-l-blue-500' }
}

export type ErrorToastProps = {
  severity: ErrorSeverity
  message: string
  title?: string
  requestId?: string
  /** Los campos que el servidor nombró al rechazar el guardado. Ver `failedFields`. */
  fields?: string[]
  actions?: ErrorAction[]
  onDismiss: () => void
}

/**
 * Rich error toast content. Rendered inside sonner via `toast.custom`, so sonner
 * manages positioning/stacking/lifecycle while we own the layout. Styled to match
 * sonner's own toasts: solid `popover` surface (design-system token), border and
 * shadow, with a severity-colored left accent. The severity is signaled by the
 * left border + icon, not by a translucent tint, so the toast is fully opaque.
 */
export function ErrorToast({
  severity,
  message,
  title,
  requestId,
  fields,
  actions = [],
  onDismiss
}: ErrorToastProps) {
  const { icon: Icon, accent, border } = SEVERITY_STYLES[severity]

  return (
    <div
      role="alert"
      className={cn(
        'flex w-full gap-3 rounded-md border border-l-4 p-4 shadow-lg',
        'bg-popover text-popover-foreground',
        border
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', accent)} aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && <p className="text-sm font-semibold text-popover-foreground">{title}</p>}
        <p className="text-sm text-muted-foreground">{message}</p>
        {fields && fields.length > 0 && (
          <p className="font-mono text-[11px] break-all text-muted-foreground/80">
            Campos: {fields.join(', ')}
          </p>
        )}
        {requestId && (
          <p className="font-mono text-[11px] text-muted-foreground/80">Referencia: {requestId}</p>
        )}

        {actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {actions.map((action) => {
              const ActionIcon = action.icon
              return (
                <Button
                  key={action.label}
                  type="button"
                  size="sm"
                  variant={action.primary ? 'default' : 'outline'}
                  className="h-7 gap-1.5 px-2.5 text-xs"
                  onClick={() => {
                    action.onClick()
                    onDismiss()
                  }}
                >
                  {ActionIcon && <ActionIcon className="h-3.5 w-3.5" />}
                  {action.label}
                </Button>
              )
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        aria-label="Cerrar"
        onClick={onDismiss}
        className="-m-1 h-6 w-6 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
